/**
 * firmaAramaService.js — Firma arama + hızlı firma kaydı
 *
 * Kendine ait bir route'u yok; Stand oluşturma/düzenleme popup'larındaki firma
 * seçicilerinin (FirmaSearchSelect) ve "Yeni Firma" popup'ının (CreateFirmaModal)
 * servis katmanı.
 * Base URL: {JAVA_BACKEND_API_URL}/firma
 * Auth: getAuthHeader() — Basic Auth + X-user-Id + X-token
 *
 * Kaynak: docs/guides/FE-Firma-create-match-search-guide.md
 *
 * Neden ayrı bir dosya (firmaService.js dururken): firmaService.js modül seviyesinde
 * `USE_MOCK_DATA = true` ile kilitli ve `forceLive` seçeneği yok — Stand Listesi'nin
 * canlı test fuarı (298/299) deseniyle uyuşmuyor (bkz. StandListPage.jsx
 * `isLiveTestFuar`). Buradaki uçlar (cards/create/esle/types) o desenle, mock ve
 * canlı yollar yan yana duracak şekilde yazıldı.
 */

import axios from 'axios';
import { JAVA_BACKEND_API_URL, getAuthHeader } from '../Components/Contexts/BackendConstants';
import i18n from "../i18n";

const FIRMA_API = `${JAVA_BACKEND_API_URL}/firma`;

// ─── MOCK MODU ────────────────────────────────────────────────────────────────
export const USE_MOCK_DATA = true; // Backend hazır olunca false yap

const mockDelay = (ms = 200) => new Promise((r) => setTimeout(r, ms));

// ─── Sabitler (bkz. rehber "Constants") ───────────────────────────────────────
export const TURKIYE_COUNTRY_ID = 190;

// firma.country_id NOT NULL; "ülkesi bilinmiyor" için tek sentinel 0.
export const UNKNOWN_COUNTRY_ID = 0;

export const FIRMA_NATION = { YERLI: 1, YABANCI: 2 };

// firma_type id'leri. TASERON bir ARALIK (>= 4), tek id değil — bkz. rehber.
export const FIRMA_TYPE = { KATILIMCI: 1, ORGANIZATOR: 2, DEKORATOR: 3, TASERON: 4 };

// firma_name VARCHAR(255); backend hiçbir uzunluk kontrolü yapmıyor (255'i aşan
// değer MySQL tarafında 500 olarak patlar), bu yüzden sınır frontend'de.
export const FIRMA_NAME_MAX = 255;

let _mockFirmalar = [
  { firmaId: 49858, firmaName: 'Örnek Yapı A.Ş.', firmaShortname: 'ÖRNEKYAPI', isForbidden: 0 },
  { firmaId: 49859, firmaName: 'Mock Katılımcı Sanayi A.Ş. (mock)', firmaShortname: 'MOCKKAT', isForbidden: 0 },
  { firmaId: 48832, firmaName: 'DIGICOM', firmaShortname: 'DIGICOM', isForbidden: 0 },
  { firmaId: 48833, firmaName: 'CETINLER', firmaShortname: 'CETINLER', isForbidden: 0 },
  { firmaId: 34413, firmaName: 'HD MİMARLIK', firmaShortname: 'HDMIM', isForbidden: 0 },
  { firmaId: 47431, firmaName: 'KOLEKTİF FUARCILIK', firmaShortname: 'KOLEKTIF', isForbidden: 0 },
  { firmaId: 19497, firmaName: 'FUARAY MİMARLIK', firmaShortname: 'FUARAY', isForbidden: 0 },
];
let _mockNextFirmaId = 49900;

const mockFirmaTypeRows = [
  { firmaTypeId: 1, firmaTypeValue: 'Katılımcı', parentId: 0, firmaTypeName: 'Katılımcı', languageId: 1 },
  { firmaTypeId: 2, firmaTypeValue: 'Organizatör', parentId: 0, firmaTypeName: 'Organizatör', languageId: 1 },
  { firmaTypeId: 3, firmaTypeValue: 'Dekoratör', parentId: 0, firmaTypeName: 'Dekoratör', languageId: 1 },
  { firmaTypeId: 4, firmaTypeValue: 'Taşeron', parentId: 0, firmaTypeName: 'Taşeron', languageId: 1 },
  { firmaTypeId: 5, firmaTypeValue: 'Nakliye', parentId: 0, firmaTypeName: 'Nakliye', languageId: 1 },
  { firmaTypeId: 7, firmaTypeValue: 'Temizlik', parentId: 0, firmaTypeName: 'Temizlik', languageId: 1 },
  // 14 = Güvenlik: hiçbir dilde firma_type_text satırı YOK, firmaTypeName null gelir.
  { firmaTypeId: 14, firmaTypeValue: 'Güvenlik', parentId: 0, firmaTypeName: null, languageId: null },
];

// ─── Yardımcılar ──────────────────────────────────────────────────────────────

// /cards ham SQL map döndürüyor: isForbidden SAYI (0/1). /list ise DTO üzerinden
// `forbidden` BOOLEAN döndürür — aynı kolon, iki yazım, iki tip; tek parser
// paylaşmayın (bkz. rehber). Burada /cards normalize ediliyor.
function normalizeFirmaCard(row) {
  return {
    firmaId: row.firmaId ?? row.firma_id ?? null,
    firmaName: row.firmaName ?? row.firma_name ?? '',
    firmaShortname: row.firmaShortname ?? row.firma_shortname ?? null,
    firmaInsertdate: row.firmaInsertdate ?? null,
    isForbidden: Number(row.isForbidden ?? row.forbidden ?? 0) === 1,
  };
}

/**
 * Firma arama — global (fuar bağımsız), SAYFALI.
 * GET /firma/cards?search=&page=&size=&sortBy=&sortDir=
 *
 * `search` firma_id / firma_name / firma_shortname üçünde birden LIKE %term%.
 * Boş `search` her şeyi döner ama sayfalı olduğu için güvenli.
 *
 * ⚠️ Neden /cards, POST /searchFirmaStand değil: searchFirmaStand SAYFASIZ ve boş
 * metinde 28.970 satır dönüyor (rehber, 2026-08-25 ölçümü) — bir arama kutusunun
 * arkasına konacak uç değil. /cards hem sayfalı, hem de yeni oluşturulan firmanın
 * id'sini bulmak için zaten kullandığımız uç.
 */
export async function searchFirmalar(
  { text = '', page = 0, size = 10, sortBy = 'firmaName', sortDir = 'ASC' } = {},
  { forceLive = false } = {}
) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    const q = String(text).toLowerCase().trim();
    const filtered = _mockFirmalar.filter((f) =>
      !q ||
      String(f.firmaId).includes(q) ||
      f.firmaName.toLowerCase().includes(q) ||
      (f.firmaShortname || '').toLowerCase().includes(q)
    );
    const start = page * size;
    return {
      content: filtered.slice(start, start + size).map(normalizeFirmaCard),
      totalElements: filtered.length,
      totalPages: Math.max(1, Math.ceil(filtered.length / size)),
      number: page,
      size,
    };
  }

  try {
    const res = await axios.get(`${FIRMA_API}/cards`, {
      params: { search: text || '', page, size, sortBy, sortDir },
      headers: getAuthHeader(),
    });
    const data = res.data || {};
    return {
      content: (data.content || []).map(normalizeFirmaCard),
      totalElements: data.totalElements ?? 0,
      totalPages: data.totalPages ?? 1,
      number: data.number ?? page,
      size: data.size ?? size,
    };
  } catch (error) {
    console.error('Error searching firmalar:', error);
    throw error;
  }
}

/**
 * Rol-sıralı firma arama — backend #22. `firmaTypeId`'yi daha önce taşımış firmalar önce gelir.
 * GET /firma/cards/by-role?firmaTypeId=&search=&page=&size=
 *
 * #21: Yeni/Düzenle Fuar'da bir role firma eklerken FirmaSearchSelect'in kullandığı arama.
 */
export async function searchFirmalarByRole(
  { text = '', firmaTypeId, page = 0, size = 10 } = {},
  { forceLive = false } = {}
) {
  if (firmaTypeId == null) throw new Error(i18n.t("services:firmaAramaService.searchFirmalarByRoleFirmaTypeIdZorunlu"));

  if (USE_MOCK_DATA && !forceLive) {
    // Mock veri rol geçmişi taşımıyor — sıralama önemsiz, normal aramaya düşülüyor.
    return searchFirmalar({ text, page, size }, { forceLive });
  }

  try {
    const res = await axios.get(`${FIRMA_API}/cards/by-role`, {
      params: { firmaTypeId: Number(firmaTypeId), search: text || '', page, size },
      headers: getAuthHeader(),
    });
    const data = res.data || {};
    return {
      content: (data.content || []).map(normalizeFirmaCard),
      totalElements: data.totalElements ?? 0,
      totalPages: data.totalPages ?? 1,
      number: data.number ?? page,
      size: data.size ?? size,
    };
  } catch (error) {
    console.error('Error searching firmalar by role:', error);
    throw error;
  }
}

/**
 * Yeni firma kaydı. Hiçbir fuara bağlamaz — bağlamak için esleFirmaToFuar().
 * POST /firma/create
 *
 * ⚠️ Uç YENİ ID'yi DÖNMÜYOR (`data: null`). Rehberin önerdiği geçici çözüm: hemen
 * ardından /cards'ı firmaInsertdate DESC ile arayıp az önce yazılan satırı bulmak.
 * O zincir burada kapsüllendi — çağıran taraf tek bir Promise bekliyor ve
 * `{ firmaId, firmaName, ... }` alıyor.
 * #197 (2026-10-10): backend artık `data.firmaId` dönüyor ve `taxNumber` alıyor; arama zinciri
 * yalnızca eski backend'e karşı yedek.
 *
 * ⚠️ firmaNation ve countryId pratikte ZORUNLU (kolonlar NOT NULL, INSERT her
 * kolonu açıkça yazıyor → eksik alan explicit NULL olarak gidiyor ve 400 dönüyor).
 * ⚠️ Sayılar JSON SAYISI olmalı — tırnaklı "1" bu uçta 500 veriyor ((Integer) cast'i
 * try/catch'in DIŞINDA).
 * ⚠️ Mükerrer ad kontrolü YOK — aynı gövdeyi iki kez göndermek iki firma yaratır.
 * Çift göndermeyi UI engelliyor (CreateFirmaModal submit sırasında butonu kilitler).
 */
// WAS (backlog #197 öncesi): { firmaName, firmaShortname = null, firmaNation, countryId, oldFirmaId = null }
export async function createFirma(
  { firmaName, firmaShortname = null, firmaNation, countryId, oldFirmaId = null, taxNumber = null },
  { forceLive = false } = {}
) {
  const payload = {
    firmaName: String(firmaName || '').trim(),
    firmaShortname: firmaShortname ? String(firmaShortname).trim() : null,
    oldFirmaId: oldFirmaId === null || oldFirmaId === '' ? null : Number(oldFirmaId),
    // countryId 190 (Türkiye) backend'de firmaNation'ı 1'e ZORLAR, ne gönderirsek
    // gönderelim — aynı kuralı burada da uyguluyoruz ki dönen kayıt tutarlı olsun.
    firmaNation: Number(countryId) === TURKIYE_COUNTRY_ID ? FIRMA_NATION.YERLI : Number(firmaNation),
    countryId: Number(countryId),
    // #197: opsiyonel. Boşluksuz gönderilir; backend Yerli için 10/11 rakam, Yabancı için ≤50 karakter ister.
    taxNumber: taxNumber ? String(taxNumber).replace(/\s+/g, '') || null : null,
  };

  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    const created = {
      firmaId: _mockNextFirmaId++,
      firmaName: payload.firmaName,
      firmaShortname: payload.firmaShortname,
      isForbidden: 0,
    };
    _mockFirmalar = [created, ..._mockFirmalar];
    return normalizeFirmaCard(created);
  }

  // WAS (backlog #197 öncesi): yanıt okunmuyordu, id aşağıdaki aramayla bulunuyordu.
  // try {
  //   await axios.post(`${FIRMA_API}/create`, payload, { headers: getAuthHeader() });
  // } catch (error) {
  //   console.error('Error creating firma:', error);
  //   throw error;
  // }
  let createdData = null;
  try {
    const response = await axios.post(`${FIRMA_API}/create`, payload, { headers: getAuthHeader() });
    createdData = response?.data?.data ?? null;
  } catch (error) {
    console.error('Error creating firma:', error);
    throw error;
  }

  // #197: backend artık yeni id'yi dönüyor ({ data: { firmaId, firmaName, firmaShortname, taxNumber } }).
  // Eski backend'e karşı (data: null) aşağıdaki arama zinciri yedek olarak duruyor.
  if (createdData?.firmaId) {
    return {
      ...normalizeFirmaCard({
        firmaId: createdData.firmaId,
        firmaName: createdData.firmaName ?? payload.firmaName,
        firmaShortname: createdData.firmaShortname ?? payload.firmaShortname,
        isForbidden: 0,
      }),
      taxNumber: createdData.taxNumber ?? payload.taxNumber,
    };
  }

  // Yeni id'yi geri okuma adımı. Ad belirsizse (aynı adda eski kayıtlar varsa)
  // firmaInsertdate DESC'in ilk satırı az önce yazdığımızdır.
  try {
    const found = await searchFirmalar(
      { text: payload.firmaName, page: 0, size: 5, sortBy: 'firmaInsertdate', sortDir: 'DESC' },
      { forceLive }
    );
    const exact = found.content.find((f) => f.firmaName === payload.firmaName);
    return exact || found.content[0] || { firmaId: null, ...payload };
  } catch (error) {
    // Firma YAZILDI ama id'yi çözemedik — çağıran taraf ayırt edebilsin diye
    // firmaId null dönüyoruz, hata FIRLATMIYORUZ: aksi halde kullanıcı "başarısız"
    // sanıp tekrar dener ve ikinci bir firma kaydı oluşur (dedupe yok).
    console.error('Firma created but id lookup failed:', error);
    return { firmaId: null, ...payload };
  }
}

/**
 * Firmayı bir FUARA bağlar (rol + yerleşim + kullanıcı erişimi, tek transaction).
 * POST /firma/esle
 *
 * ⚠️ Bu uç firmayı STANDA bağlamaz — firma_to_stand'e hiç dokunmaz. Standa bağlamak
 * standOverviewService.assignFirmaToStand (admin yolu) ya da firmaAtamaTalep akışı.
 * ⚠️ Dedupe YOK, unique key YOK: iki kez çağırmak iki rol satırı yazar ve firmayı
 * POST /edit-for-fuar'a kapatır. Butonu ilk başarıdan sonra kilitleyin.
 */
export async function esleFirmaToFuar(
  { firmaId, fuarId, firmaTypeId, salonId = null, standId = null },
  { forceLive = false } = {}
) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    return { message: 'Firma successfully matched to fuar.', result: true, data: null };
  }

  try {
    const res = await axios.post(
      `${FIRMA_API}/esle`,
      {
        firmaId: Number(firmaId),
        fuarId: Number(fuarId),
        firmaTypeId: Number(firmaTypeId),
        salonId: salonId === null || salonId === '' ? null : Number(salonId),
        standId: standId === null || standId === '' ? null : Number(standId),
      },
      { headers: getAuthHeader() }
    );
    return res.data;
  } catch (error) {
    console.error('Error matching firma to fuar:', error);
    throw error;
  }
}

/**
 * Firma tipleri — rol seçicileri için.
 * GET /firma/types
 *
 * Uç (tip, dil) çifti başına BİR satır döner (2026-08-10'da 15 tip → 29 satır),
 * yani ham liste dropdown'a doğrudan verilemez. Burada tip başına tekilleştirilip
 * TR adı (languageId 1) seçiliyor.
 * ⚠️ 14 (Güvenlik) hiçbir dilde metin satırına sahip değil → firmaTypeName null.
 * Etiket firmaTypeValue'ya düşürülüyor, aksi halde dropdown'da BOŞ bir satır çıkar.
 */
export async function getFirmaTypes({ forceLive = false } = {}) {
  let rows;
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    rows = mockFirmaTypeRows;
  } else {
    try {
      const res = await axios.get(`${FIRMA_API}/types`, { headers: getAuthHeader() });
      rows = res.data || [];
    } catch (error) {
      console.error('Error fetching firma types:', error);
      throw error;
    }
  }

  const byId = new Map();
  rows.forEach((r) => {
    const id = r.firmaTypeId;
    if (id == null) return;
    const existing = byId.get(id);
    // TR (languageId 1) her zaman kazanır; yoksa ilk gelen satır tutulur.
    if (!existing || r.languageId === 1) {
      byId.set(id, {
        firmaTypeId: id,
        parentId: r.parentId ?? 0,
        firmaTypeValue: r.firmaTypeValue ?? null,
        label: (r.languageId === 1 ? r.firmaTypeName : null) || r.firmaTypeName || r.firmaTypeValue || i18n.t("services:firmaAramaService.tip", { id }),
      });
    }
  });

  return Array.from(byId.values()).sort((a, b) => a.firmaTypeId - b.firmaTypeId);
}
